import type {SupportTicketState} from "../../api/models";

// Support tickets: the customer's pages, and the ticket page administrators share with them.
export const support = {
    title: 'Support',
    newTicket: 'New ticket',
    intro: "Open a ticket about a product you hold a license for. We'll email you when we reply or the ticket's status changes. Support is provided in English only.",
    none: "You haven't opened any support tickets.",
    columns: {
        subject: 'Subject',
        product: 'Product',
        status: 'Status',
        lastActivity: 'Last activity'
    },
    unknownProduct: 'Unknown product',
    // A ticket that isn't about a license.
    generalTicket: 'General question',

    states: {
        'pending': 'Pending',
        'in-review': 'In Review',
        'waiting-for-info': 'Waiting For Info',
        'resolved': 'Resolved',
        'closed': 'Closed'
    } satisfies Record<SupportTicketState, string>,
    // The same, inside a sentence ("This ticket is closed").
    statesInSentence: {
        'pending': 'pending',
        'in-review': 'in review',
        'waiting-for-info': 'waiting for info',
        'resolved': 'resolved',
        'closed': 'closed'
    } satisfies Record<SupportTicketState, string>,
    // Why a ticket's status changed, by the API's reason ids (src/api/models.ts SupportTicketReasons).
    reasons: {
        'opened': 'Opened',
        'under-investigation': 'Under investigation',
        'information-received': 'Information received',
        'reopened': 'Reopened',
        'need-more-details': 'Need more details',
        'need-logs-or-screenshots': 'Need logs or screenshots',
        'need-steps-to-reproduce': 'Need steps to reproduce',
        'fixed-in-release': 'Fixed in a release',
        'workaround-provided': 'Workaround provided',
        'configuration-issue': 'Configuration issue',
        'question-answered': 'Question answered',
        'closed-by-customer': 'Closed by customer',
        'no-response': 'No response from customer',
        'duplicate': 'Duplicate',
        'not-a-defect': 'Not a defect',
        'out-of-scope': 'Out of scope',
        'license-revoked': 'License revoked',
        'license-transferred': 'License transferred',
        'checking-outlet': 'Checking the outlet',
        'need-outlet-confirmation': 'Need confirmation from the outlet',
        'press-answered': 'Answered',
        'review-license-issued': 'Review license issued',
        'not-a-fit': 'Not a fit'
    } as { [id: string]: string },

    attachments: {
        label: 'Attachments <muted>(optional)</muted>',
        help: 'Screenshots (PNG or JPG) and log files (TXT), up to 10 MB each and {max} per message. Pictures have their location and camera details removed.',
        // Read out by screen readers, and the remove button's label.
        removed: 'Removed {name}',
        remove: 'Remove {name}',
        uploading: 'Uploading attachments',
        // Why a chosen file was left out (src/components/supportFiles.ts).
        wrongType: '{name}: only PNG, JPG and TXT files can be attached.',
        empty: '{name} is empty.',
        tooLarge: '{name} is larger than 10 MB.',
        tooMany: 'At most {max} files can be attached: {name} was left out.'
    },

    thread: {
        // A signed-out visitor, when their name isn't known.
        visitor: 'You',
        // The label screen readers give the list of messages, and of a message's files.
        messages: 'Messages',
        attachments: 'Attachments',
        fileGone: 'That file is no longer available.',
        // Who wrote a message from the support team.
        support: 'Support',
        supportAuthor: ' ({username})',
        // The ticket's first message.
        synopsis: 'Synopsis',
        // For screen readers, between an attachment's name and size.
        download: ', download,'
    },

    history: {
        title: 'History',
        // {date}, then who changed the status.
        automatically: '{date}, automatically',
        by: '{date}, by {username}'
    },

    newTicketPage: {
        title: 'New support ticket',
        back: 'Support',
        // Signed out: anyone can ask (before buying, an educational license, a problem), and follow the ticket by email.
        signedOutIntro: "Ask us anything: a question before buying, an educational license, or a problem. We'll email you a private link to your ticket, where you'll see our replies. Have an account? <link>Sign in</link> first, so it's kept with your others.",
        name: 'Your name',
        email: 'Email address',
        emailHelp: "We'll send the link to your ticket here. It's the only way to see it, so check it's right.",
        filesLater: 'You can add screenshots or log files from the link we email you.',
        checkEmail: "Thank you: your ticket is open. We've sent a link to it to {email}. Open it to see the ticket, add details or files, and read our replies.",
        tooMany: "You've opened a lot of tickets today. Please try again tomorrow, or add to one of your open tickets.",
        tooManySignedOut: 'Too many tickets have been opened from your network today. Please try again tomorrow.',
        unavailable: "Support tickets can't be opened right now. Please try again later.",
        turnstileFailed: "The check that you're not a bot didn't go through. Please do it again, then send.",
        turnstileUnavailable: "The check that you're not a bot couldn't load. Check your connection, or try another browser.",
        license: 'What is this about?',
        chooseLicense: 'Choose a license, or a general question',
        // A license in the list: the product, edition and key.
        licenseOption: '{product}, {edition} ({key})',
        generalOption: 'Not about a license (a question, an educational license…)',
        subject: 'Subject',
        body: 'How can we help?',
        bodyHelp: 'For a problem: what you were doing, what happened, what you expected, and the product version and operating system.',
        // Also the contact page's. A public statement (see legal/terms.md, Support): keep them in step.
        englishOnly: 'Please write in English: support is provided in English only.',
        submit: 'Open ticket',
        opened: 'Your ticket has been opened.',
        problems: {
            name: 'Enter your name.',
            email: 'Enter your email address.',
            license: 'Choose the license this is about, or a general question.',
            subject: 'Enter a short summary.',
            body: 'Tell us how we can help.'
        }
    },

    // /contact, the top bar's and footer's Contact us: a ticket that isn't about a license, mostly from people who
    // haven't bought anything yet. Its fields, checks and errors are newTicketPage's.
    contactPage: {
        title: 'Send us a message',
        // For search results and link previews.
        description: 'Questions before buying, educational licenses, or anything else: send Tristar Advanced Software a message. No account needed.',
        signedOutIntro: "Ask us anything: a question before buying, an educational license, or anything else. We'll email you a private link where you'll see our replies. Have an account? <link>Sign in</link> first, so it's kept with your support tickets.",
        emailHelp: "We'll send a link to your message here. It's the only way to see our replies, so check it's right.",
        checkEmail: "Thank you: your message has been sent. We've emailed a link to it to {email}. Open it to read our replies, or to add details or files.",
        tooMany: "You've sent a lot of messages today. Please try again tomorrow, or add to one of your open tickets.",
        tooManySignedOut: 'Too many messages have been sent from your network today. Please try again tomorrow.',
        unavailable: "Messages can't be sent right now. Please try again later.",
        submit: 'Send',
        sent: 'Your message has been sent.'
    },

    ticket: {
        // Signed out, opened without the ticket's emailed link (or with one that's expired).
        needsLink: "To see this ticket, open the link in the email we sent you about it. Links last 90 days: each new email about the ticket brings a fresh one. Is it in your account? <link>Sign in</link>.",
        // Signed out, with a link that no longer works.
        linkExpired: "This link has expired, or isn't for this ticket. Open the link in the most recent email about it: each one brings a fresh link.",
        allTickets: 'All tickets',
        back: 'Support',
        noSuchTicket: 'There is no such ticket.',
        // {state} is from statesInSentence.
        cantReply: "This ticket is {state}, so it can't be replied to. If you still need help, <link>open a new ticket</link>.",
        details: {
            product: 'Product',
            customer: 'Customer',
            // A ticket opened signed out: the email the visitor gave (not an account).
            customerEmail: '{email} (no account)',
            license: 'License',
            viewLicense: 'View license',
            opened: 'Opened',
            lastActivity: 'Last activity'
        },
        reply: {
            label: 'Reply',
            required: 'Write a reply.',
            tooMany: "You've sent a lot of replies recently. Please wait a while and try again.",
            submit: 'Send reply',
            sent: 'Reply sent.'
        },
        // Administrators only.
        changeState: {
            title: 'Change status',
            newStatus: 'New status',
            chooseStatus: 'Choose a status',
            statusRequired: 'Choose a status.',
            reason: 'Reason',
            chooseReason: 'Choose a reason',
            reasonRequired: 'Choose a reason.',
            statusFirst: 'Choose a status first.',
            submit: 'Change status',
            changed: 'Status changed to {state}.'
        },
        close: {
            button: 'Close ticket',
            title: 'Close this ticket?',
            text: "Close it if you no longer need help with this. You won't be able to reply to it afterwards, but you can open a new ticket.",
            closed: 'Ticket closed.'
        },
        // Administrators only.
        delete: {
            button: 'Delete ticket',
            title: 'Delete this ticket?',
            alreadyDeleted: 'The ticket was already deleted.',
            text: "The ticket, its {messages}{attachments} will be deleted for good, for the customer too. This can't be undone.",
            messages: { one: '{count} message', other: '{count} messages' },
            // Empty without attachments.
            attachments: { one: ' and {count} attachment', other: ' and {count} attachments' },
            deleted: 'Ticket deleted.'
        }
    }
};

export type SupportMessages = typeof support;
